<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import type { ContractManagementContractFileInDb } from '@/api';
import GaiaMenuSurface from '@/components/design/base/GaiaMenuSurface.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaListItem from '@/components/design/composite/GaiaListItem.vue';
import { IconDocument } from '@/components/design/icons';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { contractFileOpenRoute } from '@/router/links';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';

/**
 * A contract's files, listed on hover over whatever is slotted in (the vault
 * row's file-count badge). Fetched the first time it opens, then kept.
 */
const props = defineProps<{ contractUid: string }>();

/** How many names the list shows; the rest are counted. */
const LIMIT = 20;

const router = useRouter();

const files = ref<ContractManagementContractFileInDb[] | null>(null);
const total = ref(0);
const loading = ref(false);
const failed = ref(false);

async function onShow(show: boolean): Promise<void> {
  if (!show || files.value !== null || loading.value) return;
  loading.value = true;
  failed.value = false;
  try {
    const result = await ContractFilesService.getContractFilesForContract(props.contractUid, {
      pageSize: LIMIT,
      sortKey: 'created_at',
      sortDirection: 'asc',
    });
    files.value = result.results;
    total.value = result.result_count;
  } catch {
    failed.value = true;
  } finally {
    loading.value = false;
  }
}

function fileName(file: ContractManagementContractFileInDb): string {
  return file.vault_file?.current_revision?.file_name ?? file.description ?? 'Untitled file';
}
</script>

<template>
  <gaia-popover trigger="hover" placement="bottom-start" :delay="250" @update:show="onShow">
    <template #trigger>
      <slot />
    </template>
    <gaia-menu-surface width="300px">
      <gaia-text
        variant="label"
        size="micro"
        tone="faint"
        tag="div"
        :style="{ padding: '2px 10px 8px' }"
      >
        Files in this contract
      </gaia-text>
      <n-flex v-if="loading" justify="center" :size="0" :style="{ padding: '8px 0' }">
        <gaia-spinner :size="16" />
      </n-flex>
      <gaia-text
        v-else-if="failed"
        size="control"
        tone="subtle"
        tag="div"
        :style="{ padding: '0 10px 4px' }"
      >
        Couldn’t load the files.
      </gaia-text>
      <n-scrollbar v-else-if="files" style="max-height: 260px">
        <gaia-list-item
          v-for="file in files"
          :key="file.uid"
          size="dense"
          @click="router.push(contractFileOpenRoute(file))"
        >
          <template #leading>
            <n-icon :component="IconDocument" :size="15" :color="gaiaNeutral.textMuted" />
          </template>
          <gaia-text truncate>{{ fileName(file) }}</gaia-text>
        </gaia-list-item>
      </n-scrollbar>
      <gaia-text
        v-if="files && total > files.length"
        size="micro"
        tone="faint"
        tag="div"
        :style="{ padding: '6px 10px 2px' }"
      >
        and {{ total - files.length }} more
      </gaia-text>
    </gaia-menu-surface>
  </gaia-popover>
</template>
